import { createReducer, createSelector } from '@reduxjs/toolkit';
import { all, call, fork, takeEvery, takeLatest } from 'redux-saga/effects';
import { HEARING_TYPE, NOTIFICATIONTYPE_TYPE } from '../../constants/api';
import { API_REDUCER } from '../../constants/store';
import {
  getInvitationNotificationsForHearingError,
  getInvitationNotificationsForHearingInitialize,
  getInvitationNotificationsForHearingSuccess,
  sendNotificationInitialize,
} from '../actions/api';
import { NotificationConnector } from '../connectors/notificationConnector';
import { getEntityId, getRelId } from '../helpers/apiHelpers';
import { updateDtoList } from '../helpers/reducerHelpers';
import { generateGetAllApiCall } from '../helpers/sagaHelpers';
import {
  getRequestData,
  selectAllFromApiState,
} from '../helpers/selectorHelpers';
import { notificationTypeByTypeSelector } from './notificationTypeSlice';

// -------- Overview --------
// The following endpoints exist
//  - GET /hearing/{hearingId}/invitationNotifications
//  - POST /hearing/{hearingId}/notification/{notificationType}/send

// -------- Reducer --------
const notificationReducer = createReducer(
  {
    all: [],
    isFetchingNotifications: false,
  },
  (builder) => {
    builder
      .addCase(getInvitationNotificationsForHearingInitialize, (state) => {
        state.isFetchingNotifications = true;
      })
      .addCase(getInvitationNotificationsForHearingError, (state) => {
        state.isFetchingNotifications = false;
      })
      .addCase(getInvitationNotificationsForHearingSuccess, (state, action) => {
        const newState = Object.assign({}, state, {
          isFetchingNotifications: false,
        });

        const notifications = getRequestData(action);
        return updateDtoList(newState, 'all', notifications);
      });
  },
);

// --------- Sagas ----------
export function* notificationSaga() {
  yield all([
    fork(getInvitationNotificationsForHearing),
    fork(sendNotificationSaga),
  ]);
}

function* getInvitationNotificationsForHearing() {
  yield takeEvery(
    getInvitationNotificationsForHearingInitialize.type,
    generateGetAllApiCall(NotificationConnector, function* (connector, action) {
      return yield call(
        [connector, connector.getInvitationNotifications],
        action.payload.hearingId,
      );
    }),
  );
}

function* sendNotificationSaga() {
  yield takeLatest(
    sendNotificationInitialize.type,
    generateGetAllApiCall(
      NotificationConnector,
      function* (connector, action) {
        return yield call(
          [connector, connector.sendNotification],
          action.payload.hearingId,
          action.payload.notificationType,
        );
      },
      true,
    ),
  );
}

// -------- Selectors --------
const selectAllNotifications = (state) =>
  selectAllFromApiState(state, API_REDUCER.NOTIFICATION);

const allNotificationsSelector = createSelector(
  selectAllNotifications,
  (notifications) => notifications,
);

const allNotificationsWithTypeSelector = (type) =>
  createSelector(
    [allNotificationsSelector, notificationTypeByTypeSelector(type)],
    (notifications, notificationType) =>
      notifications.filter(
        (n) =>
          getRelId(n, NOTIFICATIONTYPE_TYPE) === getEntityId(notificationType),
      ),
  );

const allNotificationsWithTypeForHearingIdSelector = (type, hearingId) =>
  createSelector(allNotificationsWithTypeSelector(type), (notifications) =>
    notifications.filter((n) => getRelId(n, HEARING_TYPE) === hearingId),
  );

export { allNotificationsWithTypeForHearingIdSelector };
export default notificationReducer;
